import React, { useState, useEffect } from "react";
import { Plus, CheckCircle, Clock, X } from "lucide-react";
import { formatCurrency } from "../utils/mockData";
import { sponsorsApi } from "../utils/api";

function SponsorModal({ onClose, onSave }) {
  const [form, setForm] = useState({ name: "", amount: "", contact: "", item: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSave({
      ...form,
      amount: Number(form.amount) || 0,
      status: "confirmed",
      logo: form.name.split(" ").map((w) => w[0]).join("").substring(0, 2).toUpperCase(),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl z-10">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900">Add Sponsor</h2>
          <button onClick={onClose}><X size={18} className="text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Sponsor Name *</label>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Sponsor name" required
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Item / Purpose</label>
            <input type="text" value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })}
              placeholder="e.g. Idol, Decoration, Sound System"
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Amount (₹)</label>
              <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0"
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Contact Number</label>
              <input type="tel" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })}
                placeholder="Phone"
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
          </div>
          <button type="submit"
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-xl transition-colors">
            Add Sponsor
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Sponsors() {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    sponsorsApi.list()
      .then((res) => setSponsors(res.items || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const totalSponsorship = sponsors.reduce((sum, s) => sum + Number(s.amount || 0), 0);
  const confirmedCount = sponsors.filter((s) => s.status === "confirmed").length;

  const handleSave = async (sponsor) => {
    try {
      const saved = await sponsorsApi.create(sponsor);
      setSponsors([...sponsors, saved.item || sponsor]);
    } catch {
      setSponsors([...sponsors, sponsor]);
    }
    setShowModal(false);
  };

  const toggleStatus = async (id) => {
    const sponsor = sponsors.find((s) => s.id === id);
    if (!sponsor) return;
    const updated = { ...sponsor, status: sponsor.status === "confirmed" ? "pending" : "confirmed" };
    setSponsors(sponsors.map((s) => s.id === id ? updated : s));
    sponsorsApi.update(id, { status: updated.status }).catch(console.error);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-4 lg:p-6 space-y-4 fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Sponsors</h1>
          <p className="text-sm text-gray-500">{confirmedCount} confirmed • {formatCurrency(totalSponsorship)} secured</p>
        </div>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2.5 rounded-xl text-sm shadow-lg shadow-orange-500/20">
          <Plus size={16} /> Add Sponsor
        </button>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Sponsor</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Item / Purpose</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Contact</th>
                <th className="text-right text-xs font-semibold text-gray-500 px-4 py-3">Amount</th>
                <th className="text-center text-xs font-semibold text-gray-500 px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sponsors.map((sponsor) => (
                <tr key={sponsor.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center text-xs font-black text-orange-600 flex-shrink-0">
                        {sponsor.logo}
                      </div>
                      <span className="text-sm font-semibold text-gray-800">{sponsor.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{sponsor.item || "—"}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{sponsor.contact || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-bold text-gray-900">
                      {Number(sponsor.amount) > 0 ? formatCurrency(Number(sponsor.amount)) : "In-Kind"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => toggleStatus(sponsor.id)}
                      className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg font-semibold transition-colors ${
                        sponsor.status === "confirmed"
                          ? "text-green-600 bg-green-50 hover:bg-green-100"
                          : "text-yellow-600 bg-yellow-50 hover:bg-yellow-100"
                      }`}>
                      {sponsor.status === "confirmed"
                        ? <><CheckCircle size={11} /> Confirmed</>
                        : <><Clock size={11} /> Pending</>}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile List */}
        <div className="md:hidden divide-y divide-gray-100">
          {sponsors.map((sponsor) => (
            <div key={sponsor.id} className="px-4 py-3 flex items-center gap-3">
              <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center text-sm font-black text-orange-600 flex-shrink-0">
                {sponsor.logo}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{sponsor.name}</p>
                <p className="text-xs text-gray-400">{sponsor.item ? `${sponsor.item} • ` : ""}{sponsor.contact || "No contact"}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold text-gray-900">
                  {Number(sponsor.amount) > 0 ? formatCurrency(Number(sponsor.amount)) : "In-Kind"}
                </p>
                <button onClick={() => toggleStatus(sponsor.id)}
                  className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                    sponsor.status === "confirmed" ? "text-green-600 bg-green-50" : "text-yellow-600 bg-yellow-50"
                  }`}>
                  {sponsor.status === "confirmed" ? "Confirmed" : "Pending"}
                </button>
              </div>
            </div>
          ))}
        </div>

        {sponsors.length === 0 && (
          <div className="text-center py-12 text-gray-400 text-sm">No sponsors added yet</div>
        )}
      </div>

      {showModal && <SponsorModal onClose={() => setShowModal(false)} onSave={handleSave} />}
    </div>
  );
}
